<template>
  <div class="upload-area">
    <div
      v-if="replyComment && !isVisible"
      class="upload-area__reply-comment"
    >
      <div class="upload-area__reply-comment__header">
        <icon
          data="@icon/reply-icon-lg.svg"
          :fill="true"
          color="var(--monochrome05to04)"
          width="var(--icon-32)"
          height="var(--icon-32)"
        />
        <BaseSubHeading
          size="medium"
          weight="600"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="upload-area__reply-comment__owner"
        >
          {{ replyComment?.owner?.fullName }}
        </BaseSubHeading>
        <button
          type="button"
          class="upload-area__reply-comment__close"
          @click="$emit('clearReplyComment')"
        >
          <icon
            data="@icon/close.svg"
            :fill="false"
            width="var(--icon-24)"
            height="var(--icon-24)"
          />
        </button>
      </div>
      <div class="upload-area__reply-comment__content-wrapper">
        <div class="upload-area__reply-comment__gap" />
        <ReferencedText
          v-if="checkHtml(replyComment.structuredDescription.html)"
          :html="replyComment.structuredDescription.html"
          :resetStyle="true"
          class="upload-area__reply-comment__content"
        />
        <div
          v-else-if="replyComment.fileMetadata?.length"
          class="upload-area__reply-comment__file"
        >
          <icon
            data="@icon/file.svg"
            :fill="true"
            width="var(--icon-24)"
            height="var(--icon-24)"
            color="var(--monochrome05to04)"
          />
          <div class="upload-area__reply-comment__file-info">
            <BaseSubHeading
              size="small"
              weight="500"
              color="monochrome-07"
              class="is-ellipsis upload-area__reply-comment__file-name"
            >
              {{ replyComment.fileMetadata[0].name }}
            </BaseSubHeading>
            <BaseSubHeading
              size="small"
              weight="500"
              color="monochrome-05"
              darkColor="monochrome-04"
              class="upload-area__reply-comment__file-size"
            >
              {{ getFileSize(replyComment.fileMetadata[0].size) }}
            </BaseSubHeading>
          </div>
        </div>
      </div>
    </div>
    <div
      ref="uploadDropTarget"
      :class="[
        { 'is-file-exist': files.length },
        { 'is-expand': isVisible },
        { 'is-drop-active': dropUpload?.dropActive }
      ]"
      class="upload-area__in"
    >
      <div
        v-if="dropUpload?.dropActive"
        class="upload-area__drop-content"
      >
        <span class="upload-area__drop-image">
          <icon
            :data="uploadImage"
            original
            :fill="false"
            width="100%"
            height="100%"
            class="upload-area__drop-image-icon"
            style="stroke: none"
          />
        </span>
        <div class="upload-area__drop-text">
          <BaseSubHeading
            :size="isVisible ? 'large' : 'medium'"
            weight="500"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            Drag Files Here
          </BaseSubHeading>
          <BaseSubHeading
            size="small"
            weight="500"
            color="monochrome-06"
          >
            up to {{ store.getters[Getters.GET_MAX_UPLOAD_SIZE_LABEL].toUpperCase() }} each
          </BaseSubHeading>
        </div>
      </div>
      <div
        v-if="isVisible"
        class="upload-area__edit"
      >
        <Editor
          ref="expandedEditor"
          :triggerHandleInputOnMount="true"
          :isReadonly="isReadonly"
          :extensions="extensions"
          :targetConfig="targetConfig"
          :referenceFileTargetConfig="referenceFileTargetConfig"
          :inComment="true"
          :focusEditor="true"
          :value="editingCommentHtml"
          :placeholder="placeholder"
          class="task__full__editor"
          @handleInput="handleInput"
          @createData="handleInput"
        >
        </Editor>
        <EmojiDropdown
          class="upload-area__emoji-dropdown upload-area__emoji-dropdown--expanded"
          @onSelect="onSelectEmojiExpanded"
          @click.stop
        />
      </div>

      <EditorInput
        v-else
        id="comments"
        class="upload-area__content"
        :class="{ 'is-disabled': isReadonly }"
        :placeholder="placeholder"
        :focusEditor="!isReadonly && !disableFocus"
        :focusEnd="!!structuredDescription.html"
        :triggerFocus="isReadonly || disableFocus ? false : triggerFocus"
        :value="structuredDescription.html"
        :targetConfig="targetConfig"
        :referenceFileTargetConfig="referenceFileTargetConfig"
        :triggerHandleInputOnMount="true"
        :customExtensions="customExtensions"
        :referencesEnabled="referencesEnabled"
        @handleInput="handleInput"
        @onPasteFile="onPasteFile"
      >
        <template #footer="{ editor }">
          <div class="upload-area__footer-actions">
            <button
              type="button"
              class="upload-area__expand-button"
              @click="visible"
            >
              <icon
                data="@icon/expand-open.svg"
                width="var(--icon-16)"
                height="var(--icon-16)"
              />
            </button>

            <EmojiDropdown
              class="upload-area__emoji-dropdown"
              @onSelect="emoji => onSelectEmoji(emoji, editor)"
            />
          </div>
        </template>
      </EditorInput>
    </div>
    <file-upload
      v-if="!isReadonly"
      ref="dropUpload"
      :inputId="`${fileUploadInputId}-drop`"
      class="upload-area__drop-upload"
      :multiple="true"
      :drop="uploadDropTarget"
      @inputFilter="inputFilter"
      @update:modelValue="onDropUploadFiles"
    />
  </div>
  <div
    v-if="(isEditMode && editingCommentFiles.length) || (!isEditMode && files.length)"
    class="upload-area__list custom-scroll"
  >
    <template v-if="isEditMode">
      <FileItem
        v-for="item in editingCommentFiles"
        :key="`existing-${item.id}`"
        :file="item"
        :removeButton="true"
        :preventShow="isVisible"
        @remove="removeEditingFile"
      />
    </template>
    <template v-else>
      <FileItem
        v-for="item in files"
        :key="`new-${item.id}`"
        :file="item"
        :removeButton="true"
        :preventShow="isVisible"
        @remove="removeFile"
      />
    </template>
  </div>
  <div class="comment_buttons">
    <div class="upload-area__wrap">
      <file-upload
        v-if="!isReadonly"
        :inputId="fileUploadInputId"
        :value="files"
        name="file-upload"
        class="upload-area__button"
        :multiple="true"
        :drop="true"
        :dropDirectory="true"
        @inputFilter="inputFilter"
        @change="onFileInputChange"
      >
        <button
          type="button"
          class="upload-area__add-file"
        >
          <icon
            data="@icon/attach.svg"
            :fill="false"
            width="1.5em"
            height="1.5em"
          />
          Add File
        </button>
      </file-upload>
      <div
        v-else
        class="upload-area__button"
        :class="{ 'is-disabled': isReadonly }"
      >
        <button
          type="button"
          class="upload-area__add-file"
        >
          <icon
            data="@icon/attach.svg"
            :fill="false"
            width="1.5em"
            height="1.5em"
          />
          Add File
        </button>
      </div>
    </div>
    <CommentTimestampInput
      v-if="showDemoVideoTimestamp"
      v-tooltip.bottom="demoVideoTimestampTooltipConfig"
      class="links-divider__item"
      :value="demoVideoTimestampInputValue"
      :enabled="demoVideoTimestampEnabled"
      :disabled="isDemoVideoTimestampDisabled"
      :durationSeconds="demoVideoTimestampConfig?.durationSeconds"
      @change="onDemoVideoTimestampChange"
    />
    <div
      v-if="isVisible"
      class="upload-area__buttons is-flex"
    >
      <button
        type="button"
        class="comment__submit-button links-divider__item back-button"
        @click="cancelEdit"
      >
        Back
      </button>
      <button
        type="button"
        class="comment__submit-button send-button"
        @click="onSubmitValidation"
      >
        <span>Send</span>
        <icon
          data="@icon/paperplane.svg"
          :fill="true"
          color="var(--accent-03)"
          width="var(--icon-24)"
          height="var(--icon-24)"
        />
      </button>
    </div>
    <button
      v-if="!isVisible"
      type="button"
      class="comment__submit-button"
      :class="{ 'is-disabled': isReadonly }"
      @click="onSubmitValidation"
    >
      <span>Send</span>
      <icon
        data="@icon/paperplane.svg"
        :fill="true"
        color="var(--accent-03)"
        width="var(--icon-24)"
        height="var(--icon-24)"
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { v4 as uuidv4 } from "uuid"
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from "vue"
import { useStore } from "vuex"

import CommentTimestampInput from "@/components/Comment/CommentTimestampInput.vue"
import EditorInput from "@/components/Editor/EditorInput.vue"
import Editor from "@/components/Editor/index.vue"
import EmojiDropdown from "@/components/Emoji/EmojiDropdown.vue"
import FileItem from "@/components/File/FileItem.vue"
import { ReferencedText } from "@/components/Reference"

import formatBytes from "@/helpers/formatBytes"
import {
  Comment,
  EditorData,
  EmojiType,
  FileMetaData,
  RepliedOnComment,
  StructuredDescription,
  TypeConfig,
  User,
  VUFile
} from "@/models"
import { CustomEmoji } from "@/plugins/extensions/CustomEmoji"
import { PasteFile } from "@/plugins/extensions/PasteFile"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashFileLink,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { Getters } from "@/store/getters"
import { replaceHtmlTags } from "@/utils/helpers"
import { getTimestampSeconds } from "@/utils/timestampUtil"

import type { Editor as TiptapEditor } from "@tiptap/vue-3"

export interface DemoVideoTimestampConfig {
  value: string
  durationSeconds?: number
  disabled?: boolean
  disabledTooltip?: boolean
}

interface UploadAreaProps {
  savedMessage?: string
  replyComment?: RepliedOnComment | null
  targetConfig?: TypeConfig | null
  referenceFileTargetConfig?: TypeConfig | null
  savedFiles?: VUFile[]
  triggerFocus?: boolean
  isReadonly?: boolean
  disableFocus?: boolean
  placeholder?: string
  demoVideoTimestampConfig?: DemoVideoTimestampConfig | null
  referencesEnabled?: boolean
}

interface FileUploadRef {
  clear: () => unknown
  dropActive: boolean
}

interface EditorRef {
  editor: TiptapEditor | null
}

interface EditorInputData {
  html: string
  json: unknown
  getEmailText: () => string
}

interface EmojiOption {
  type: EmojiType
}

interface SubmitPayload {
  message: string
  text: string
  json: string
  files: VUFile[]
  repliedOnComment: RepliedOnComment | null
  demoVideoTimestamp: Comment["demoVideoTimestamp"]
}

interface SaveMessagePayload {
  message: string
  files: VUFile[]
}

interface CancelEditPayload {
  comment: Comment | null
  currentHtml: string
  currentJson: string
}

interface SaveEditPayload {
  comment: Comment
  html: string
  text: string
  json: string
  files: VUFile[]
  remainingFiles: FileMetaData[]
  demoVideoTimestamp: Comment["demoVideoTimestamp"]
}

interface PendingSubmissionDraft {
  structuredDescription: StructuredDescription
  files: VUFile[]
  isVisible: boolean
  editingCommentHtml: string
  demoVideoTimestampEnabled: boolean
}

const props = withDefaults(defineProps<UploadAreaProps>(), {
  savedMessage: "",
  replyComment: null,
  targetConfig: null,
  referenceFileTargetConfig: null,
  savedFiles: () => [],
  triggerFocus: false,
  isReadonly: false,
  disableFocus: false,
  placeholder: "Leave comment",
  demoVideoTimestampConfig: null,
  referencesEnabled: true
})

const emit = defineEmits<{
  (e: "visible"): void
  (e: "editorExpanded", isExpanded: boolean): void
  (e: "submit", payload: SubmitPayload): void
  (e: "saveMessage", payload: SaveMessagePayload): void
  (e: "clearReplyComment"): void
  (e: "cancelEditComment", payload: CancelEditPayload): void
  (e: "saveEditComment", payload: SaveEditPayload): void
  (e: "editCommentFilesChange", payload: { files: FileMetaData[] }): void
  (e: "demoVideoTimestampChange", seconds: number): void
}>()
const store = useStore()
const dropUpload = ref<FileUploadRef | null>(null)
const uploadDropTarget = ref<HTMLElement | null>(null)
const expandedEditor = ref<EditorRef | null>(null)
const isVisible = ref(false)
const isEditMode = ref(false)
const editingComment = shallowRef<Comment | null>(null)
const editingCommentHtml = ref("")
const editingCommentFiles = ref<FileMetaData[]>([])
const structuredDescription = ref(new StructuredDescription())
const files = ref<VUFile[]>([])
const pendingSubmissionDraft = shallowRef<PendingSubmissionDraft | null>(null)
const demoVideoTimestampValue = ref<Comment["demoVideoTimestamp"]>(null)
const demoVideoTimestampEnabled = ref(false)
const demoVideoTimestampDisabledTooltip =
  "Videos embedded via links are displayed in iframes, which limits the interaction available to third-party applications."

const customExtensions = computed(() => {
  return props.referencesEnabled && props.referenceFileTargetConfig
    ? [ReferenceAllUser, ReferenceHashFileLink]
    : []
})

const isDark = computed(() => store.getters[Getters.THEME_IS_DARK] as boolean)
const uploadImage = computed(() =>
  isDark.value ? require("@icon/upload-image-dark.svg") : require("@icon/upload-image.svg")
)

const extensions = computed(() => {
  return [
    ...(props.referencesEnabled
      ? [
          ReferenceAt,
          ReferenceHashProject,
          ReferenceHashMilestone,
          ReferenceHashEpic,
          ReferenceHashIdea,
          ReferenceHashTeam,
          ReferenceHashSprint,
          ReferenceHashItem,
          ReferenceHashTask,
          ReferenceHashTip,
          ReferenceLink,
          ReferenceHashFile,
          ReferenceHashFileLink
        ]
      : []),
    PasteFile(onPasteFile),
    CustomEmoji
  ]
})

const hasCommentValue = computed(() => {
  return hasCommentText.value || files.value.length > 0 || editingCommentFiles.value.length > 0
})

const hasCommentText = computed(() => replaceHtmlTags(structuredDescription.value.html).length > 0)

const showDemoVideoTimestamp = computed(() => !!props.demoVideoTimestampConfig && !isVisible.value)

const isDemoVideoTimestampDisabled = computed(
  () => props.demoVideoTimestampConfig?.disabled ?? false
)

const isDemoVideoTimestampSelected = computed(
  () =>
    !!props.demoVideoTimestampConfig &&
    !isDemoVideoTimestampDisabled.value &&
    demoVideoTimestampEnabled.value
)

const demoVideoTimestampInputValue = computed(() =>
  demoVideoTimestampEnabled.value || isEditMode.value
    ? demoVideoTimestampValue.value
    : (props.demoVideoTimestampConfig?.value ?? null)
)

const demoVideoTimestampTooltipConfig = computed(() => ({
  content: demoVideoTimestampDisabledTooltip,
  disabled: !props.demoVideoTimestampConfig?.disabledTooltip,
  triggers: ["click"],
  theme: "info-tooltip",
  popperClass: "upload-area__demo-video-timestamp-popper"
}))

const fileUploadInputId = computed(() => {
  const targetId = props.targetConfig?.targetId || "comment"
  const context = props.demoVideoTimestampConfig ? "demo" : "default"

  return `${targetId}-${context}-file-upload`
})

function createStructuredDescription(html: string, json: unknown) {
  const editorData = new EditorData()
  editorData.setHtml(html)

  return new StructuredDescription({
    html,
    json: JSON.stringify(json),
    text: editorData.getEmailText()
  })
}

function handleInput(editorData: EditorInputData) {
  structuredDescription.value = createStructuredDescription(editorData.html, editorData.json)
  if (props.targetConfig?.type === "EPIC") saveMessage()
}

function onDemoVideoTimestampChange(seconds: number | null, enabled: boolean) {
  demoVideoTimestampValue.value = seconds
  demoVideoTimestampEnabled.value = enabled

  if (!isDemoVideoTimestampSelected.value || seconds === null) {
    return
  }

  emit("demoVideoTimestampChange", seconds)
}

function inputFilter(newFile: VUFile, oldFile: VUFile, prevent: (prevent?: boolean) => boolean) {
  if (newFile && !oldFile) {
    if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
      return prevent()
    }

    if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
      return prevent()
    }
  }
}

function visible() {
  isVisible.value = !isVisible.value
  if (isVisible.value) {
    editingCommentHtml.value = structuredDescription.value.text?.trim()
      ? structuredDescription.value.html
      : ""
  }
  emit("visible")
  emit("editorExpanded", isVisible.value)
}

function onCommentAction() {
  visible()
}

function setEditingComment(comment: Comment) {
  structuredDescription.value = new StructuredDescription({
    json: comment.structuredDescription?.json || ""
  })
  editingComment.value = comment
  editingCommentHtml.value = comment.structuredDescription?.html || ""
  editingCommentFiles.value = comment.fileMetadata ? [...comment.fileMetadata] : []
  isEditMode.value = true
  isVisible.value = true
  if (props.demoVideoTimestampConfig) {
    demoVideoTimestampEnabled.value =
      comment.demoVideoTimestamp !== null && comment.demoVideoTimestamp > 0
    demoVideoTimestampValue.value = comment.demoVideoTimestamp
  }
  emit("editorExpanded", true)
}

function refreshEditingFiles(comment: Comment) {
  editingCommentFiles.value = comment.fileMetadata ? [...comment.fileMetadata] : []
  editingComment.value = comment
}

function syncEditingCommentFiles() {
  if (!editingComment.value) return

  const comment = Comment.clone(editingComment.value)
  comment.fileMetadata = [...editingCommentFiles.value]
  editingComment.value = comment
}

function cancelEdit() {
  const comment = editingComment.value
  let currentHtml = structuredDescription.value.html || editingCommentHtml.value
  let currentJson = structuredDescription.value.json

  if (expandedEditor.value?.editor && isVisible.value) {
    currentHtml = expandedEditor.value.editor.getHTML()
    currentJson = JSON.stringify(expandedEditor.value.editor.getJSON())
  }

  if (isEditMode.value) {
    structuredDescription.value = new StructuredDescription()
  }

  isEditMode.value = false
  editingComment.value = null
  editingCommentHtml.value = ""
  editingCommentFiles.value = []
  isVisible.value = false
  emit("editorExpanded", false)
  emit("cancelEditComment", {
    comment,
    currentHtml,
    currentJson
  })
}

function finishEditingComment() {
  clear()
  isEditMode.value = false
  editingComment.value = null
  editingCommentHtml.value = ""
  editingCommentFiles.value = []
  isVisible.value = false
  emit("editorExpanded", false)
}

function finishSubmittingComment() {
  pendingSubmissionDraft.value = null
  clear()
  if (isVisible.value) {
    isVisible.value = false
    emit("editorExpanded", false)
  }
}

function startSubmittingComment() {
  pendingSubmissionDraft.value = {
    structuredDescription: new StructuredDescription({ ...structuredDescription.value }),
    files: [...files.value],
    isVisible: isVisible.value,
    editingCommentHtml: editingCommentHtml.value,
    demoVideoTimestampEnabled: demoVideoTimestampEnabled.value
  }

  clear()
  if (isVisible.value) {
    isVisible.value = false
    emit("editorExpanded", false)
  }
}

function restoreSubmittingComment() {
  if (!pendingSubmissionDraft.value) return

  const draft = pendingSubmissionDraft.value
  structuredDescription.value = new StructuredDescription({ ...draft.structuredDescription })
  files.value = [...draft.files]
  editingCommentHtml.value = draft.editingCommentHtml || draft.structuredDescription.html
  isVisible.value = draft.isVisible
  demoVideoTimestampEnabled.value = draft.demoVideoTimestampEnabled
  pendingSubmissionDraft.value = null

  saveMessage()
  emit("editorExpanded", isVisible.value)
}

function saveEdit() {
  if (!editingComment.value) return

  emit("saveEditComment", {
    comment: editingComment.value,
    html: structuredDescription.value.html,
    text: structuredDescription.value.text,
    json: structuredDescription.value.json,
    files: files.value,
    remainingFiles: editingCommentFiles.value,
    demoVideoTimestamp: getDemoVideoTimestampForSubmit()
  })
}

function clear() {
  structuredDescription.value = new StructuredDescription()
  files.value = []
  demoVideoTimestampEnabled.value = false
  demoVideoTimestampValue.value = null
  saveMessage()
}

function onSubmit() {
  handleChanges()
}

function onSubmitValidation() {
  if (!hasCommentValue.value || props.isReadonly) {
    return
  }

  if (isEditMode.value) {
    saveEdit()
    return
  }

  onSubmit()
}

function removeFile(file: FileMetaData) {
  files.value = files.value.filter(item => item.id !== file.id)
}

function removeEditingFile(file: FileMetaData) {
  editingCommentFiles.value = editingCommentFiles.value.filter(item => item.id !== file.id)
  syncEditingCommentFiles()
  emit("editCommentFilesChange", {
    files: editingCommentFiles.value
  })
}

function handleChanges() {
  if (expandedEditor.value?.editor && isVisible.value) {
    structuredDescription.value = createStructuredDescription(
      expandedEditor.value.editor.getHTML(),
      expandedEditor.value.editor.getJSON()
    )
  }

  emit("submit", {
    message: structuredDescription.value.html,
    text: structuredDescription.value.text,
    json: structuredDescription.value.json,
    files: files.value,
    repliedOnComment: props.replyComment,
    demoVideoTimestamp: getDemoVideoTimestampForSubmit()
  })
}

function getDemoVideoTimestampForSubmit() {
  const timestampSeconds = demoVideoTimestampValue.value

  if (!hasCommentValue.value || !isDemoVideoTimestampSelected.value || timestampSeconds === null) {
    return null
  }

  return timestampSeconds
}

function saveMessage() {
  emit("saveMessage", {
    message: structuredDescription.value.html,
    files: files.value
  })
}

function addFiles(vueFiles: VUFile[]) {
  if (isEditMode.value && editingComment.value) {
    const tempFiles = vueFiles.map(file =>
      FileMetaData.create({
        id: file.id.toString(),
        name: file.name,
        size: file.size,
        targetType: file.name,
        createdDate: new Date().toISOString(),
        owner: User.createDefault(),
        custom: true,
        pendingFile: file.file
      })
    )
    editingCommentFiles.value = [...editingCommentFiles.value, ...tempFiles]
    syncEditingCommentFiles()
    emit("editCommentFilesChange", {
      files: editingCommentFiles.value
    })
    return
  }

  files.value = [...files.value, ...vueFiles].sort((a, b) => {
    const nameA = a.name.toLowerCase()
    const nameB = b.name.toLowerCase()
    if (nameA < nameB) {
      return -1
    }
    if (nameA > nameB) {
      return 1
    }
    return 0
  })
}

function onUploadFiles(filesList: FileList | File[]) {
  addFiles(
    Array.from(filesList).map(
      file =>
        new VUFile({
          id: uuidv4(),
          name: file.name,
          size: file.size,
          type: file.type,
          file
        })
    )
  )
}

function onFileInputChange(event: Event) {
  const input = event.target as HTMLInputElement
  if (input.files) {
    onUploadFiles(input.files)
  }
}

function onDropUploadFiles(payload: VUFile[]) {
  if (!payload.length) return

  addFiles(payload)
  dropUpload.value?.clear()
}

function onPasteFile(file: File) {
  onUploadFiles([file])
}

function onSelectEmoji(emoji: EmojiOption, editor: TiptapEditor) {
  editor.chain().focus().insertEmoji(emoji).run()
}

function onSelectEmojiExpanded(emoji: EmojiOption) {
  if (expandedEditor.value?.editor) {
    expandedEditor.value.editor.chain().focus().insertEmoji(emoji).run()
  }
}

function checkHtml(html: string) {
  return !!replaceHtmlTags(html)
}

function getFileSize(size: number) {
  return formatBytes(size)
}

onMounted(() => {
  if (props.savedMessage) {
    structuredDescription.value = new StructuredDescription({
      html: props.savedMessage
    })
  }
  if (props.savedFiles.length) {
    files.value = [...props.savedFiles]
  }
})

watch(
  [() => props.demoVideoTimestampConfig?.value, demoVideoTimestampEnabled],
  ([value]) => {
    if (!props.demoVideoTimestampConfig) return
    if (isEditMode.value) return
    if (!demoVideoTimestampEnabled.value) return
    demoVideoTimestampValue.value = value ? getTimestampSeconds(value) : null
  },
  { immediate: true }
)

watch(isDemoVideoTimestampDisabled, isDisabled => {
  if (isDisabled) {
    demoVideoTimestampEnabled.value = false
  }
})

onUnmounted(() => {
  saveMessage()
})

defineExpose({
  onCommentAction,
  setEditingComment,
  refreshEditingFiles,
  finishEditingComment,
  finishSubmittingComment,
  startSubmittingComment,
  restoreSubmittingComment
})
</script>
